import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import './GameEntry.css';

function JodiGame({ gameName, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const [availableDigits, setAvailableDigits] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredDigits, setFilteredDigits] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDigit, setSelectedDigit] = useState('');
  const [points, setPoints] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameType = location.state?.gameTypeId || '';
  const gameId = location.state?.gameId || '';
  const gameNameFromState = location.state?.gameName || '';
  const gameTypeLabel = 'Jodi Digit';

  // Fetch game data from API
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();

        if (data.game_ank && data.game_ank.length > 0) {
          // console.log('API Response for game-ank:', data);
          const gameAnkData = data.game_ank[0];
          
          // Parse jodi_digits - it's a JSON string
          let jodiDigits = [];
          if (gameAnkData.jodi_digit) {
            try {
              const parsed = typeof gameAnkData.jodi_digit === 'string' 
                ? JSON.parse(gameAnkData.jodi_digit) 
                : gameAnkData.jodi_digit;
              jodiDigits = parsed;
            } catch (parseError) {
              console.error('Error parsing jodi:', parseError);
              jodiDigits = [];
            }
          }
          
          // console.log('Fetched Jodi digits:', jodiDigits);
          setAvailableDigits(jodiDigits);
        }
        setError('');
      } catch (err) {
        setError('Failed to load game data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Filter digits based on search input
  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredDigits([]);
      return;
    }

    if (!Array.isArray(availableDigits) || availableDigits.length === 0) {
      setFilteredDigits([]);
      return;
    }

    // availableDigits is already an array of jodi digits
    let filtered = availableDigits
      .filter(digit => digit.toString().includes(searchInput.trim()))
      .slice(0, 10); // Show max 10 results

    setFilteredDigits(filtered);
  }, [searchInput, availableDigits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-navigate home when Jodi bid window closes (5 min before open time)
  useEffect(() => {
    if (isOpenBidClosed()) {
      navigate('/');
      return;
    }
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = openTime.split(':').map(Number);
    const msUntilClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;
    const t = setTimeout(() => navigate('/'), msUntilClose);
    return () => clearTimeout(t);
  }, [openTime, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDigitSelect = (digit) => {
    setSelectedDigit(digit);
    setSearchInput('');
    setShowDropdown(false);
  };

  const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

  const getKolkata = () => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  };

  // Jodi bids close 5 minutes before the open time
  const isOpenBidClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = openTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  const handleAddEntry = async () => {
    if (!selectedDigit || !points.trim()) {
      toast.error('Please select a digit and enter points');
      return;
    }

    if (isOpenBidClosed()) {
      toast.error('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      navigate('/');
      return;
    }

    try {
      // Get player data from localStorage
      const playerData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const formData = new FormData();
      formData.append('game_id', gameId);
      formData.append('game_name', gameNameFromState);
      formData.append('game_type', gameType);
      formData.append('player_id', playerData.id || '');
      formData.append('session', '');
      formData.append('bid_amount', points);
      formData.append('bid_number', selectedDigit);

      const response = await fetch('https://backend.mainbazar777.site/api/play-game', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.balance === false) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
      } else if (response.ok && data.success) {
        toast.success(`Bid Placed: ${selectedDigit} for ${points} rs`);
        
        // Refresh wallet balance after successful bid
        const playerData = JSON.parse(localStorage.getItem('user') || '{}');
        if (playerData?.id) {
          updateWalletBalance(playerData.id);
        }
        
        setSelectedDigit('');
        setPoints('');
        setSearchInput('');
      } else {
        toast.error(data.message || 'Failed to place bid');
      }
    } catch (err) {
      console.error('Error placing bid:', err);
      toast.error('Failed to place bid. Please try again.');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  if (loading) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Loading...</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="loading-message">Loading game data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Error</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry">
      <header className="game-entry-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || gameTypeLabel}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      <div className="game-content">
        <div className="game-info-section">
          <h2 className="game-type-heading">{gameTypeLabel}</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
            <div className="info-item">
              <label>Status</label>
              <input type="text" value="N/A" readOnly disabled />
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        <div className="input-section">
          <div className="input-row">
            <div className="input-item" ref={dropdownRef}>
              <label>Select Jodi (2 Digits):</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder="Enter Jodi..."
                  value={searchInput || selectedDigit}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/[^\d]/g, '').slice(0, 2);
                    setSearchInput(digitsOnly);
                    setShowDropdown(true);
                    if (!digitsOnly) {
                      setSelectedDigit('');
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="digit-input"
                  autoComplete="off"
                />
                {showDropdown && filteredDigits.length > 0 && (
                  <div className="dropdown-menu">
                    {filteredDigits.map((digit, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => handleDigitSelect(digit)}
                      >
                        {digit}
                      </div>
                    ))}
                  </div>
                )}
                {showDropdown && searchInput && filteredDigits.length === 0 && (
                  <div className="dropdown-menu">
                    <div className="dropdown-item disabled">No results found</div>
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds:</label>
              <input
                type="number"
                placeholder="Funds"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
              />
            </div>
          </div>

          <button className="add-btn" onClick={handleAddEntry}>
            Place Bid
          </button>
        </div>
      </div>

    </div>
  );
}

export default JodiGame;
